import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { Sheet } from './overlay';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useNavigationProfile } from '../context/NavigationContext';

/**
 * ContextMenu ("Mehr" menu) — responsive navigation sheet.
 *
 * Desktop (>= 1280px): right-side drawer (500px)
 * Mobile/Tablet (< 1280px): full bottom sheet (95vh)
 *
 * Built on <Sheet> overlay component with responsive side switching.
 * See: docs/architecture/OVERLAY_ARCHITECTURE.md §4.8.1
 */
export default function ContextMenu({
  isOpen,
  onClose,
  user,
  theme,
  onSetTheme,
  onResetToSystem,
  onNewSession,
  onLanguageChange,
  onOpenSettings,
}) {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const { profile } = useNavigationProfile();

  // hamburger_items / profile_menu_items from navigation profile control visibility
  const hamburgerItems = profile?.hamburger_items;
  const profileMenuItems = profile?.profile_menu_items;
  const isItemVisible = (itemId) => {
    // If no profile restrictions, show all
    if (!hamburgerItems && !profileMenuItems) return true;
    // Check both lists
    const all = [...(hamburgerItems || []), ...(profileMenuItems || [])];
    return all.length === 0 || all.includes(itemId);
  };

  const initials = (user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  // Responsive: right drawer on desktop, full bottom sheet on mobile/tablet
  const side = isDesktop ? 'right' : 'bottom';
  const size = isDesktop ? 'md' : 'full';

  return (
    <Sheet.Root open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <Sheet.Content side={side} size={size} title={t('nav.more', { defaultValue: 'Mehr' })}>
        <Sheet.Body>
          {/* User header */}
          <div className="px-2 pt-1 pb-4 border-b border-[var(--aif-border-default)]">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[var(--aif-color-primary)] flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold text-lg">{initials}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[var(--aif-text-primary)] truncate">{user?.name || 'User'}</div>
                <div className="text-sm text-[var(--aif-text-muted)] truncate">{user?.email || ''}</div>
              </div>
            </div>
          </div>

          {/* Menu items — content & tools (profile/settings/account items moved to AccountMenu) */}
          <div className="py-2">
            {isItemVisible('new_chat') && <MenuItem
              icon={<PlusIcon />}
              label={t('menu.newChat')}
              onClick={() => { onNewSession(); onClose(); }}
            />}
            {isItemVisible('chat_history') && <MenuItem
              icon={<HistoryIcon />}
              label={t('menu.chatHistory')}
              onClick={() => onOpenSettings('history')}
            />}

            <div className="h-px bg-[var(--aif-border-default)] my-2 mx-6" />

            {isItemVisible('farm') && <MenuItem
              icon={<FarmIcon />}
              label={t('farm.title')}
              onClick={() => onOpenSettings('farm')}
            />}
            {isItemVisible('documents') && <MenuItem
              icon={<DocIcon />}
              label={t('menu.documents')}
              onClick={() => onOpenSettings('documents')}
            />}
            {isItemVisible('brain-dump') && <MenuItem
              icon={<span className="text-base">🧠</span>}
              label={t('menu.brainDump', 'Notizen')}
              onClick={() => onOpenSettings('brain-dump')}
            />}
            {isItemVisible('storage') && <MenuItem
              icon={<span className="text-base">💾</span>}
              label={t('menu.storage', 'Speicher')}
              onClick={() => onOpenSettings('storage')}
            />}
            {isItemVisible('shares') && <MenuItem
              icon={<ShareIcon />}
              label={t('menu.sharedLinks')}
              onClick={() => onOpenSettings('shares')}
            />}

            <div className="h-px bg-[var(--aif-border-default)] my-2 mx-6" />

            {/* Theme picker — 4 options inline */}
            <ThemePicker theme={theme} onSetTheme={onSetTheme} onResetToSystem={onResetToSystem} t={t} />

            {/* Language selector inline */}
            <div className="px-6 py-3 flex items-center gap-4">
              <span className="w-5 h-5 text-[var(--aif-text-muted)]"><GlobeIcon /></span>
              <LanguageSelector onLanguageChange={onLanguageChange} />
            </div>
          </div>
        </Sheet.Body>
      </Sheet.Content>
    </Sheet.Root>
  );
}

function MenuItem({ icon, label, onClick, subtle, danger, badge }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 px-6 py-3 text-left hover:bg-[var(--aif-bg-elevated)] transition active:bg-[var(--aif-bg-input)]"
    >
      <span className={`w-5 h-5 flex-shrink-0 ${danger ? 'text-[var(--aif-color-danger)]' : 'text-[var(--aif-text-muted)]'}`}>
        {icon}
      </span>
      <span className={`text-sm flex-1 ${
        danger ? 'text-[var(--aif-color-danger)]' : subtle ? 'text-[var(--aif-text-secondary)]' : 'text-[var(--aif-text-primary)]'
      }`}>
        {label}
      </span>
      {badge && (
        <span className="text-[10px] font-medium text-[var(--aif-text-muted)] bg-[var(--aif-bg-input)] px-2 py-0.5 rounded-full flex-shrink-0">
          {badge}
        </span>
      )}
    </button>
  );
}

function ThemePicker({ theme, onSetTheme, onResetToSystem, t }) {
  const isSystem = typeof window !== 'undefined' && !localStorage.getItem('aif_theme');
  const activeKey = isSystem ? 'system' : theme;

  const options = [
    { key: 'light', label: t('settings.themeLight', 'Hell') },
    { key: 'dark', label: t('settings.themeDark', 'Dunkel') },
    { key: 'system', label: t('settings.themeSystem', 'System') },
    { key: 'outdoor', label: t('settings.themeOutdoor', 'Outdoor') },
  ];

  return (
    <div className="px-6 py-2">
      <div className="text-xs font-medium text-[var(--aif-text-muted)] mb-2">
        {t('settings.theme', 'Design')}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {options.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => opt.key === 'system' ? onResetToSystem() : onSetTheme(opt.key)}
            className={`py-1.5 text-xs rounded-md border transition-colors ${
              activeKey === opt.key
                ? 'border-[var(--aif-color-primary)] bg-[var(--aif-color-primary)]/10 text-[var(--aif-color-primary)] font-medium'
                : 'border-[var(--aif-border-default)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-tertiary)]'
            }`}
            aria-pressed={activeKey === opt.key}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Icons (heroicons style) ─────────────────────────────────
function FarmIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>;
}
function PlusIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" /></svg>;
}
function HistoryIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
}
function GlobeIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" /></svg>;
}
function DocIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>;
}
function ShareIcon() {
  return <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" /></svg>;
}
